// frontend/src/components/account/InvoicesPanel.jsx
import { 
  Table, 
  Space, 
  Button, 
  Tag, 
  Spin, 
  Empty, 
  Alert,
  Typography 
} from "antd";
import { ReloadOutlined, FileTextOutlined, EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import InvoiceStatusTag from "./InvoiceStatusTag";
import { formatDate } from "../utils/invoiceUtils";

const { Text } = Typography;

const InvoicesPanel = ({
  loadingInvoices,
  invoicesError,
  invoicesData,
  refetchInvoices,
  isDownloading,
  handleViewDetails,
  handleDownload,
  handleViewOnStripe,
}) => {
  const invoiceColumns = [
    {
      title: "Invoice #",
      dataIndex: "number",
      key: "number",
      render: (text, record) => (
        <Text strong style={{ color: "#2D5016" }}>
          {text || record.id.slice(-8)}
        </Text>
      ),
    },
    {
      title: "Date",
      dataIndex: "created",
      key: "created",
      render: (timestamp) => formatDate(timestamp),
      sorter: (a, b) => a.created - b.created,
      defaultSortOrder: "descend",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => (
        <Text strong>
          {record.currency} {amount.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <InvoiceStatusTag status={status} />,
      filters: [
        { text: "Paid", value: "paid" },
        { text: "Pending", value: "open" },
        { text: "Draft", value: "draft" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<FileTextOutlined />}
            onClick={() => handleViewDetails(record.id)}
            size="small"
          >
            Details
          </Button>
          {record.pdfUrl && (
            <Button
              type="link"
              icon={isDownloading ? <Spin size="small" /> : <DownloadOutlined />}
              onClick={() => handleDownload(record.id)}
              disabled={isDownloading}
              size="small"
              style={{ color: "#6DB33F" }}
            >
              PDF
            </Button>
          )}
          {record.hostedInvoiceUrl && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewOnStripe(record.hostedInvoiceUrl)}
              size="small"
            >
              View
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (loadingInvoices) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading invoices...</div>
      </div>
    );
  }

  if (invoicesError) {
    return (
      <Alert
        message="Error loading invoices"
        description="Failed to fetch invoices from Stripe"
        type="error"
        showIcon
        action={
          <Button size="small" onClick={refetchInvoices} icon={<ReloadOutlined />}>
            Retry
          </Button>
        }
      />
    );
  }

  if (invoicesData?.invoices?.length > 0) {
    return (
      <>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Space>
            <Tag color="green">Paid: {invoicesData.paid || invoicesData.invoices?.filter(i => i.isPaid).length || 0}</Tag>
            <Tag style={{ color: "#6DB33F", background: "#f3faf0", borderColor: "#6DB33F" }}>Pending: {invoicesData.unpaid || invoicesData.invoices?.filter(i => i.status === "open").length || 0}</Tag>
            <Tag>Total: {invoicesData.total || invoicesData.invoices?.length || 0}</Tag>
          </Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={refetchInvoices}
            size="small"
          >
            Refresh
          </Button>
        </div>
        <Table
          columns={invoiceColumns}
          dataSource={invoicesData.invoices}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 700 }}
        />
      </>
    );
  }

  return (
    <Empty
      description="No invoices found"
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    >
      <Button type="primary" onClick={refetchInvoices}>
        Refresh
      </Button>
    </Empty>
  );
};

export default InvoicesPanel;